import { useState } from "react";
import { MapPin, Pencil, Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { EmptyState, PageHeader, Panel, RowsSkeleton, Tag } from "@/components/app/kit";
import { FormDialog } from "@/components/app/FormDialog";
import { useFetch } from "@/hooks/useServerList";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const KEYS = ["name", "currency", "language", "timezone", "locale", "phone_prefix", "registration_label", "tax_label", "professional_label", "active"];

export default function Countries() {
  const { me } = useAuth();
  const isSuper = me.user.platform_role === "super_admin";
  const { data, loading, reload } = useFetch("/platform/countries");
  const ref = useFetch("/platform/reference");
  const [edit, setEdit] = useState(null);
  const fields = [
    ...(edit?.isNew ? [{ key: "code", label: "Code ISO (2 lettres)", required: true, mono: true, validate: (v) => !/^[A-Z]{2}$/.test(v || "") && "Deux lettres majuscules" }] : []),
    { key: "name", label: "Nom", required: true },
    { key: "currency", label: "Devise (ISO)", required: true, mono: true },
    { key: "language", label: "Langue", type: "select", required: true, options: (ref.data?.languages || []).map((l) => [l.code, l.name]) },
    { key: "timezone", label: "Fuseau horaire", type: "select", required: true, options: (ref.data?.timezones || []).map((z) => [z, z]) },
    { key: "locale", label: "Locale", required: true, mono: true },
    { key: "phone_prefix", label: "Indicatif" },
    { key: "registration_label", label: "Libellé immatriculation" },
    { key: "tax_label", label: "Libellé identifiant fiscal" },
    { key: "professional_label", label: "Libellé n° professionnel" },
    { key: "active", label: "Disponible à la création", type: "switch" },
  ];
  return (
    <div>
      <PageHeader eyebrow="Paramètres régionaux" title="Pays et paramètres régionaux" subtitle="Valeurs par défaut appliquées aux cabinets selon leur pays, et libellés des identifiants légaux."
        actions={isSuper && <Button onClick={() => setEdit({ isNew: true, language: "fr", active: true })} data-testid="countries-create-button"><Plus className="h-4 w-4 mr-2" />Ajouter un pays</Button>} />
      <Panel className="overflow-hidden">
        <div className="-m-5 overflow-x-auto">
          {loading ? <RowsSkeleton /> : !data?.length ? <EmptyState icon={MapPin} title="Aucun pays" /> : (
            <Table>
              <TableHeader><TableRow><TableHead>Pays</TableHead><TableHead>Devise</TableHead><TableHead>Fuseau</TableHead><TableHead>Locale</TableHead><TableHead>Identifiants légaux</TableHead><TableHead>Statut</TableHead><TableHead /></TableRow></TableHeader>
              <TableBody>
                {data.map((c) => (
                  <TableRow key={c.code} data-testid={`country-row-${c.code}`}>
                    <TableCell><span className="font-mono text-xs text-slate-400 mr-2">{c.code}</span><span className="font-medium">{c.name}</span></TableCell>
                    <TableCell className="font-mono text-sm">{c.currency}</TableCell>
                    <TableCell className="text-sm">{c.timezone}</TableCell>
                    <TableCell className="font-mono text-xs">{c.locale}</TableCell>
                    <TableCell className="text-xs text-slate-500">{c.registration_label} · {c.tax_label}</TableCell>
                    <TableCell>{c.active ? <Tag tone="green">Actif</Tag> : <Tag tone="gray">Inactif</Tag>}</TableCell>
                    <TableCell>{isSuper && <Button variant="ghost" size="sm" onClick={() => setEdit(c)} data-testid={`country-edit-${c.code}`}><Pencil className="h-4 w-4" /></Button>}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </Panel>
      <FormDialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)} wide testid="country-dialog" title={edit?.isNew ? "Ajouter un pays" : `Modifier « ${edit?.name} »`}
        initial={edit || {}} fields={fields}
        onSubmit={async (v) => {
          const body = Object.fromEntries(KEYS.map((k) => [k, v[k] ?? (k === "active" ? false : "")]));
          if (edit.isNew) await api.post("/platform/countries", { code: v.code, ...body });
          else await api.put(`/platform/countries/${edit.code}`, body);
          toast.success("Pays enregistré");
          reload();
        }} />
    </div>
  );
}
